import { signal } from '@angular/core';
import { MaskitoOptions } from '@maskito/core';

/**
 * Money formatting in the company's currency. The backend decides the currency per
 * company (its billing country: EUR, CHF) and sends it with the company as
 * `currency` and `currency_locale`; CompanyService hands it to setActiveCurrency().
 * Amounts are always in the minor unit (cents, Rappen).
 *
 * The active currency is a signal, so templates that format through these functions
 * update when it changes (an agency switching to a client shop in another country).
 */

export interface MoneyFormat {
  /** ISO 4217, e.g. "EUR" or "CHF" */
  currency: string;
  /** BCP 47 locale the amounts are written in, e.g. "de-DE" or "de-CH" */
  locale: string;
}

const DEFAULT_MONEY: MoneyFormat = { currency: 'EUR', locale: 'de-DE' };

const activeMoney = signal<MoneyFormat>(DEFAULT_MONEY);

/** The currency the dashboard shows amounts in. */
export const activeCurrency = activeMoney.asReadonly();

/** Show amounts in this currency from now on; missing values fall back to euro. */
export function setActiveCurrency(currency?: string | null, locale?: string | null): void {
  const next = {
    currency: currency || DEFAULT_MONEY.currency,
    locale: locale || DEFAULT_MONEY.locale,
  };
  const current = activeMoney();
  if (current.currency !== next.currency || current.locale !== next.locale) {
    activeMoney.set(next);
  }
}

function toUnits(amountInCents: number | null | undefined): number {
  if (amountInCents === undefined || amountInCents === null || isNaN(amountInCents)) {
    return 0;
  }
  return amountInCents / 100;
}

/**
 * Formats an amount in cents without the currency sign
 * @param amountInCents Amount in cents (e.g., 250)
 * @returns "2,50" for euro, "2.50" for francs
 */
export function formatCurrency(amountInCents: number | null | undefined): string {
  return toUnits(amountInCents).toLocaleString(activeMoney().locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Formats an amount in cents with the currency sign
 * @param amountInCents Amount in cents (e.g., 250)
 * @returns "2,50 €" for euro, "CHF 2.50" for francs
 */
export function formatCurrencyWithSymbol(amountInCents: number | null | undefined): string {
  return formatMoney(amountInCents, activeMoney());
}

/** Like formatCurrencyWithSymbol, for an amount whose currency comes with it. */
export function formatMoney(
  amountInCents: number | null | undefined,
  money: MoneyFormat = activeMoney(),
): string {
  return toUnits(amountInCents).toLocaleString(money.locale, {
    style: 'currency',
    currency: money.currency,
  });
}

/** Round amounts in texts: 200 → "2 €" / "CHF 2", 99 → "0,99 €" / "CHF 0.99". */
export function formatWholeAmount(amountInCents: number | null | undefined): string {
  const units = toUnits(amountInCents);
  const money = activeMoney();
  return units.toLocaleString(money.locale, {
    style: 'currency',
    currency: money.currency,
    minimumFractionDigits: Number.isInteger(units) ? 0 : 2,
  });
}

/** "€" or "CHF", for labels and input suffixes. */
export function currencySymbol(): string {
  const money = activeMoney();
  const parts = new Intl.NumberFormat(money.locale, {
    style: 'currency',
    currency: money.currency,
  }).formatToParts(0);
  return parts.find((p) => p.type === 'currency')?.value ?? money.currency;
}

/** "Euro" or "Schweizer Franken", for texts like "Betrag in …". */
export function currencyName(): string {
  const money = activeMoney();
  return new Intl.DisplayNames(['de'], { type: 'currency' }).of(money.currency) ?? money.currency;
}

/**
 * An amount as the value of an input: "1234,50" for euro, "1234.50" for francs,
 * without thousands separators, so it fits AMOUNT_MASK and parses back.
 */
export function formatAmountInput(amountInCents: number | null | undefined): string {
  return toUnits(amountInCents).toLocaleString(activeMoney().locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: false,
  });
}

/**
 * Input mask for an amount typed by hand: up to six digits, then a comma (or
 * point) and at most two decimals. Anything longer is refused while typing, so
 * "9,9999" can't be entered in the first place.
 */
export const AMOUNT_MASK: MaskitoOptions = {
  mask: /^\d{0,6}(?:[.,]\d{0,2})?$/,
};

/**
 * A typed amount in cents: "12,5" or "12.50" -> 1250. Thousands separators of either
 * country are understood ("1.000,50", "1’000.50", "1'000") -> 100050. Null when the
 * text isn't an amount with at most two decimals.
 */
export function parseAmountToCents(value: string | number | null | undefined): number | null {
  const text = String(value ?? '')
    .trim()
    .replace(/[\s\u00a0’']/g, '');
  const match = /^(\d+|\d{1,3}(?:([.,])\d{3})+)(?:([.,])(\d{0,2}))?$/.exec(text);
  if (!match || (match[2] && match[2] === match[3])) return null;
  const whole = Number(match[1].replace(/[.,]/g, ''));
  return whole * 100 + Number((match[4] ?? '').padEnd(2, '0'));
}

/**
 * Formats a fee rate as a percentage (e.g. 0.04 → "4 %", 0.035 → "3,5 %", in
 * Switzerland "3.5 %")
 * @param rate Share of the price (0.05 for 5 %)
 */
export function formatRate(rate: number): string {
  return `${(rate * 100).toLocaleString(activeMoney().locale, { maximumFractionDigits: 2 })} %`;
}
